"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BellRing, CircleCheck as CheckCircle2, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ResourceAlertForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-6 text-center bg-muted/50 rounded-md border border-border">
        <CheckCircle2 className="size-8 text-primary mb-3" />
        <h4 className="font-serif text-lg font-semibold text-foreground mb-1">
          Subscription confirmed
        </h4>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          {successMessage || "You are now on the notification list. You will receive an alert as soon as new research or market datasets are published."}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" size="sm" className="rounded-sm text-xs font-medium">
          Subscribe another email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <div className="w-full flex-1">
          <FormField control={control} name="email" rules={{
          required: "Business email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid business email address"
          }
        }} render={({
          field
        }) => <FormItem className="gap-1.5">
                <FormLabel className="text-xs uppercase tracking-wider font-semibold text-foreground/80">
                  Business Email
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="name@company.com" autoComplete="email" className="h-11 rounded-sm bg-card border-input px-4 text-foreground placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>} />
        </div>

        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto h-11 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-[background-color,box-shadow]">
          {isSubmitting ? "Subscribing..." : "Get alerts"}
        </Button>
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-xs text-destructive">{submitError}</p>
        </div>}

      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
        <ShieldCheck className="size-4 shrink-0 text-muted-foreground/70" />
        <span>
          Zero sponsored bias. Unsubscribe anytime per our{" "}
          <Link to="/PrivacyDataProtectionPage" className="underline underline-offset-2 hover:text-foreground transition-colors">
            privacy standards
          </Link>
          .
        </span>
      </div>
    </Form>;
}
export default function NewsletterCta() {
  const prefersReduced = useReducedMotion();
  const noteItems = [{
    id: 1,
    title: "Quarterly benchmark reports",
    desc: "Methodology-driven market intelligence delivered on release day."
  }, {
    id: 2,
    title: "Executive strategy playbooks",
    desc: "Concise frameworks built from anonymized enterprise datasets."
  }];
  return <section id="newsletter-cta" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div initial={prefersReduced ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative overflow-hidden rounded-md border border-border bg-card shadow-sm">
          {/* Masthead rule accent */}
          <div className="h-1 w-full bg-primary" />

          <div className="p-6 sm:p-10 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left Column: Heading & Context */}
              <div className="lg:col-span-6 space-y-4">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  <BellRing className="size-3.5 text-primary" />
                  <span>Publication dispatch</span>
                </div>

                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground leading-tight">
                  Resource Alert Subscription
                </h2>

                <p className="text-base text-muted-foreground leading-relaxed">
                  Get notified the moment our research analysts release new industry
                  benchmarks, strategic whitepapers, and peer-reviewed market data.
                </p>

                {/* Micro benefit points */}
                <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-border/70">
                  {noteItems.map((item, index) => <div key={item.id} data-index={index} className="space-y-1">
                      <span className="text-xs font-semibold text-foreground block">
                        {item.title}
                      </span>
                      <span className="text-xs text-muted-foreground block leading-normal">
                        {item.desc}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Right Column: Embedded Subscription Form */}
              <div className="lg:col-span-6 bg-muted/40 p-5 sm:p-7 rounded-sm border border-border/80">
                <FormProvider formId="resources_newsletter-cta_form" sectionName="resources-newsletter-cta" defaultValues={{
                email: ""
              }} validationRules={{
                email: {
                  required: true,
                  pattern: "email"
                }
              }} submitText="Get alerts" successMessage="Thank you for subscribing to MinfoMedia resource alerts." errorMessage="Unable to process your subscription. Please verify your email and try again.">
                  <ResourceAlertForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}